// Self-service account API: passwordless signup, email-OTP login, handle
// onboarding, the TOTP MFA lifecycle, passkeys, and profile updates. Signup and
// onboarding are custom /api/registry routes (they create several records
// server-side); login itself uses the SDK's native email OTP (request-otp →
// auth-with-otp) so the TOTP interception (401 {mfaId}) still applies.

import {
  create as webauthnCreate,
  get as webauthnGet,
  supported as webauthnSupported,
} from "@github/webauthn-json";
import { pb, authHeader } from "./client.js";
import { sendJSON } from "./registry.js";

const BASE = "/api/registry";

export interface SignupInput {
  email: string;
  /** Terms / data-protection consent — required. */
  termsAccepted: boolean;
}

export interface SignupResult {
  id: string;
  email: string;
}

/**
 * signup creates a passwordless, unverified account and records terms consent.
 * The caller then drives the email-OTP step (requestOtp → authWithOtp), which
 * verifies the account and logs in.
 */
export function signup(input: SignupInput): Promise<SignupResult> {
  return sendJSON<SignupResult>(`${BASE}/signup`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(input),
  });
}

/** requestOtp emails a one-time login code and returns its id. */
export async function requestOtp(email: string): Promise<string> {
  const res = await pb.collection("users").requestOTP(email);
  return res.otpId;
}

/**
 * authWithOtp completes the OTP step and stores the session token. A TOTP-enrolled
 * account instead answers 401 {mfaId}; the caller then runs completeTotpLogin.
 */
export async function authWithOtp(otpId: string, code: string): Promise<void> {
  await pb.collection("users").authWithOTP(otpId, code);
}

/** hasHandle reports whether the signed-in user has completed handle onboarding. */
export function hasHandle(): boolean {
  const rec = pb.authStore.record as Record<string, unknown> | null;
  return !!(rec && typeof rec.handle === "string" && rec.handle !== "");
}

/**
 * completeOnboarding claims the immutable @handle and provisions the personal
 * team + scope, then refreshes the cached record so the router guard and header
 * see the new handle.
 */
export async function completeOnboarding(handle: string, name?: string): Promise<void> {
  await sendJSON(`${BASE}/onboarding/handle`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...authHeader() },
    body: JSON.stringify({ handle, name: name ?? "" }),
  });
  await pb.collection("users").authRefresh();
}

interface AuthResult {
  token: string;
  record: Record<string, unknown>;
}

/**
 * completeTotpLogin finishes the second login step: it trades the mfaId from the
 * 401 OTP response plus an authenticator code for a session token, stored exactly
 * like a normal login.
 */
export async function completeTotpLogin(mfaId: string, code: string): Promise<void> {
  const res = await sendJSON<AuthResult>(`${BASE}/auth/totp/verify`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ mfaId, code }),
  });
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  pb.authStore.save(res.token, res.record as any);
}

/** mfaEnabled reads the enrollment state off the cached auth record. */
export function mfaEnabled(): boolean {
  return !!(pb.authStore.record as Record<string, unknown> | null)?.mfa_enabled;
}

/** totpEnroll starts (or restarts) a pending enrollment for the current user. */
export function totpEnroll(): Promise<{ secret: string; otpauthUrl: string }> {
  return sendJSON(`${BASE}/auth/totp/enroll`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...authHeader() },
    body: "{}",
  });
}

/** totpActivate verifies the first code and turns enforcement on. */
export async function totpActivate(code: string): Promise<void> {
  await sendJSON(`${BASE}/auth/totp/activate`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...authHeader() },
    body: JSON.stringify({ code }),
  });
  await pb.collection("users").authRefresh(); // refresh mfa_enabled on the cached record
}

/** totpDisable requires a valid current code and clears the enrollment. */
export async function totpDisable(code: string): Promise<void> {
  await sendJSON(`${BASE}/auth/totp/disable`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...authHeader() },
    body: JSON.stringify({ code }),
  });
  await pb.collection("users").authRefresh();
}

// ---- passkeys (WebAuthn) ----

export interface PasskeyInfo {
  id: string;
  name: string;
  created: string;
  lastUsed: string;
}

interface CeremonyBegin {
  ceremonyId: string;
  options: unknown; // go-webauthn CredentialCreation / CredentialAssertion ({ publicKey: … })
}

/** passkeySupported reports whether this browser can use WebAuthn passkeys. */
export function passkeySupported(): boolean {
  return webauthnSupported();
}

/**
 * registerPasskey runs a full registration ceremony (begin → navigator.credentials
 * .create → finish) and stores the credential under `name`. The ceremony id and
 * name travel as query params so the raw WebAuthn response can be the request body.
 */
export async function registerPasskey(name: string): Promise<void> {
  const begin = await sendJSON<CeremonyBegin>(`${BASE}/auth/passkey/register/begin`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...authHeader() },
    body: "{}",
  });
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const credential = await webauthnCreate(begin.options as any);
  const q = new URLSearchParams({ ceremonyId: begin.ceremonyId, name });
  await sendJSON(`${BASE}/auth/passkey/register/finish?${q}`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...authHeader() },
    body: JSON.stringify(credential),
  });
}

/**
 * loginWithPasskey runs a discoverable (usernameless) login ceremony and stores
 * the returned session token exactly like a normal login.
 */
export async function loginWithPasskey(): Promise<void> {
  const begin = await sendJSON<CeremonyBegin>(`${BASE}/auth/passkey/login/begin`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: "{}",
  });
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const assertion = await webauthnGet(begin.options as any);
  const res = await sendJSON<AuthResult>(
    `${BASE}/auth/passkey/login/finish?ceremonyId=${encodeURIComponent(begin.ceremonyId)}`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(assertion),
    },
  );
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  pb.authStore.save(res.token, res.record as any);
}

/** listPasskeys returns the current user's registered passkeys, newest first. */
export async function listPasskeys(): Promise<PasskeyInfo[]> {
  const res = await sendJSON<{ items?: PasskeyInfo[] }>(`${BASE}/auth/passkey`, {
    method: "GET",
    headers: authHeader(),
  });
  return res.items ?? [];
}

/** deletePasskey revokes a registered passkey. */
export async function deletePasskey(id: string): Promise<void> {
  await sendJSON(`${BASE}/auth/passkey/${encodeURIComponent(id)}`, {
    method: "DELETE",
    headers: authHeader(),
  });
}

// ---- personal access tokens (CLI / headless auth) ----

export interface PatInfo {
  id: string;
  name: string;
  prefix: string;
  created: string;
  lastUsed: string;
  expires: string;
}

export interface PatCreated {
  id: string;
  name: string;
  token: string; // plaintext — shown once
  prefix: string;
  expires: string;
  created: string;
}

/** createPat mints a personal access token; the plaintext token is returned once. */
export function createPat(name: string, expiresIn = 0): Promise<PatCreated> {
  return sendJSON<PatCreated>(`${BASE}/auth/pat`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...authHeader() },
    body: JSON.stringify({ name, expiresIn }),
  });
}

/** listPats returns the caller's tokens, newest first. */
export async function listPats(): Promise<PatInfo[]> {
  const res = await sendJSON<{ items?: PatInfo[] }>(`${BASE}/auth/pat`, {
    method: "GET",
    headers: authHeader(),
  });
  return res.items ?? [];
}

/** revokePat deletes a personal access token. */
export async function revokePat(id: string): Promise<void> {
  await sendJSON(`${BASE}/auth/pat/${encodeURIComponent(id)}`, {
    method: "DELETE",
    headers: authHeader(),
  });
}

/**
 * updateProfile saves the display name and refreshes the cached auth record so
 * the header (subscribed to authStore.onChange) re-renders.
 */
export async function updateProfile(name: string): Promise<void> {
  const id = pb.authStore.record?.id;
  if (!id) throw { status: 401, response: { code: "unauthorized", message: "not signed in" } };
  await pb.collection("users").update(id, { name });
  await pb.collection("users").authRefresh();
}
